import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, CountdownTimer, Loader, useVerifyingCountdown } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../utils/responsive'
import { useTranslation } from 'next-i18next'
import {
  useAppRouter,
  useConsumerCountry,
  useConsumerName,
  useConsumerEmail,
  showToast,
  useAppDispatch,
  useBiometricApplicantId,
  useSkipBiometricCheck,
} from '../../../state'
import { Route } from '../../../config/router'
import {
  BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME,
  BIOMETRIC_VERIFYING_TIME_OUT,
  BiometricHardIdCheckHookStatus,
  isCountryAllowedToSkipDuringBiometricVerification,
  isCountrySkipCreateCheck,
} from '../../../model/identity'
import { ProfileCard } from '../../ProfileCard'
import { useBiometricHardIdCheck, useIdFlowErrorHandler } from '../hooks'
import style from './style.module.css'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'

export const VerifyingBiometric: FunctionComponent = () => {
  const router = useAppRouter()
  const country = useConsumerCountry()
  const { textSize } = useConsumerSizes()
  const consumerName = useConsumerName()
  const consumerEmail = useConsumerEmail() ?? ''
  const biometricApplicantId = useBiometricApplicantId()
  const skipBiometricCheck = useSkipBiometricCheck()
  const { t } = useTranslation()
  const { handleError } = useIdFlowErrorHandler()
  const isSkipCreateCheck = isCountrySkipCreateCheck(country)
  const dispatch = useAppDispatch()
  const { status } = useBiometricHardIdCheck(biometricApplicantId, skipBiometricCheck)
  const amplitude = useAmplitudeWithEnduringEventProperties()
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const { buttonSize } = useConsumerSizes()

  const testNameSpace = 'biometric-verifying'
  const tNameSpace = 'verifyIdentity:verifying'

  const { isCountingDown: maxTimeoutCountingDown } = useVerifyingCountdown(BIOMETRIC_VERIFYING_TIME_OUT)

  const handleVerifyLaterClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  useEffect(() => {
    if (!maxTimeoutCountingDown) {
      handleError()
    }
  }, [maxTimeoutCountingDown])

  useEffect(() => {
    if (status === BiometricHardIdCheckHookStatus.INITIAL) {
      return
    }
    switch (status) {
      case BiometricHardIdCheckHookStatus.FAILED:
        router.push(`${Route.TERMINAL_ERROR}/identityVerificationFailedManual`)
        break
      case BiometricHardIdCheckHookStatus.INVALID_APPLICANT_ID:
        handleError()
        break
      case BiometricHardIdCheckHookStatus.VERIFIED:
        dispatch(showToast({ toastType: 'accountVerified' }))
        router.push(Route.HOME)
        break
      default:
        router.push(Route.HOME)
        break
    }
  }, [status])

  return (
    <>
      <div className='mb-4 mt-4'>
        <ProfileCard consumerName={consumerName} consumerEmail={consumerEmail} />
      </div>
      {isSkipCreateCheck ? (
        <Loader testNameSpace={testNameSpace} text={t(`${tNameSpace}:loader`)} />
      ) : (
        <div>
          <CountdownTimer
            loaderText={'verifyIdentity:verifying:loader'}
            loaderTextExtended={'verifyIdentity:verifying:loaderExtended'}
            loaderTimeSecondsAndMinutes={'verifyIdentity:verifying:loaderTimer:infoSecondsAndMinutes'}
            loaderTimeSeconds={'verifyIdentity:verifying:loaderTimer:infoSeconds'}
            loaderTimeExtended={'verifyIdentity:verifying:loaderExtendedInfo'}
            time={BIOMETRIC_VERIFYING_DEFAULT_WAIT_TIME}
            testNameSpace={testNameSpace}
            t={t}
            textSize={textSize}
          />
          {isCountryAllowedToSkipDuringBiometricVerification(country) && (
            <>
              <div className={style.countdownTimerVerifyLaterButtonWrapper}>
                <Button.Primary testNameSpace='verify-identity-verify-later' onClick={handleVerifyLaterClick} padding='Fluid'>
                  {t('verifyIdentity:verifyLater:buttonText')}
                </Button.Primary>
              </div>
              <VerifyLaterModal
                show={showVerifyLaterModal}
                onClose={handleModalClose}
                onVerifyNow={handleVerifyNowClick}
                onSkip={handleSkipClick}
                testNameSpace='verify-later'
                buttonSize={buttonSize}
              />
            </>
          )}
        </div>
      )}
    </>
  )
}
